import React, { useEffect, createContext, useContext, useState, useMemo, useCallback, ReactNode, useRef } from 'react';
import { generateUUID } from '@workspace/utils';
import { ToastVariant } from './toast.utils';
// eslint-disable-next-line react-refresh/only-export-components -- re-export de hook
export { useToast } from '../../hooks/useToast';

// Note: showToast and ToastVariant are available in Toast.utils.ts

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: () => void;
}

const SuccessIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14'></path>
    <polyline points='22 4 12 14.01 9 11.01'></polyline>
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='12' r='10'></circle>
    <line x1='12' y1='8' x2='12' y2='12'></line>
    <line x1='12' y1='16' x2='12' y2='16'></line>
  </svg>
);

const WarningIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'></path>
    <line x1='12' y1='9' x2='12' y2='13'></line>
    <line x1='12' y1='17' x2='12' y2='17'></line>
  </svg>
);

const InfoIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='12' r='10'></circle>
    <line x1='12' y1='16' x2='12' y2='12'></line>
    <line x1='12' y1='8' x2='12' y2='8'></line>
  </svg>
);

export const Toast: React.FC<ToastProps> = ({
  message,
  variant = 'default',
  duration = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const variantConfig: Record<string, { classes: string; icon: React.FC }> = {
    success: {
      classes: 'bg-green-50 border-green-300 text-green-800 dark:bg-green-950 dark:border-green-700 dark:text-green-100',
      icon: SuccessIcon,
    },
    error: {
      classes: 'bg-red-50 border-red-300 text-red-800 dark:bg-red-950 dark:border-red-700 dark:text-red-100',
      icon: ErrorIcon,
    },
    warning: {
      classes: 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950 dark:border-amber-700 dark:text-amber-100',
      icon: WarningIcon,
    },
    default: { classes: 'bg-card text-card-foreground border-border', icon: InfoIcon },
  };
  const { classes: variantClasses, icon: Icon } = variantConfig[variant] ?? variantConfig.default;

  return (
    <div className={`p-4 rounded-md border shadow-lg ${variantClasses} max-w-sm`}>
      <div className='flex items-center justify-between'>
        <div className='flex min-w-0 items-center gap-2'>
          <div className='flex-shrink-0'>{Icon && <Icon />}</div>
          <p className='break-words text-sm font-medium'>{message}</p>
        </div>
        <button
          onClick={onClose}
          className='ml-4 text-current opacity-70 hover:opacity-100'
          aria-label='Cerrar'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <line x1='18' y1='6' x2='6' y2='18'></line>
            <line x1='6' y1='6' x2='18' y2='18'></line>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;

// Toast Context
interface ToastContextType {
  toasts: Array<{
    id: string;
    message: string;
    variant: ToastVariant;
    duration: number;
  }>;
  addToast: (message: string, variant?: ToastVariant, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const toastContainerRef = useRef<HTMLDivElement | null>(null);
  const [toasts, setToasts] = useState<Array<{
    id: string;
    message: string;
    variant: ToastVariant;
    duration: number;
  }>>([]);

  const addToast = useCallback((message: string, variant: ToastVariant = 'default', duration: number = 5000) => {
    const id = generateUUID().slice(0, 8);
    setToasts(prev => [...prev, { id, message, variant, duration }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  useEffect(() => {
    const container = toastContainerRef.current;
    if (!container) return;
    const maxHeight = window.innerHeight * 0.5;
    if (container.scrollHeight <= maxHeight) return;

    setToasts((prev) => {
      let next = prev;
      while (next.length > 1 && container.scrollHeight > maxHeight) {
        next = next.slice(1);
        break;
      }
      return next;
    });
  }, [toasts]);

  const contextValue = useMemo(() => ({ toasts, addToast, removeToast }), [toasts, addToast, removeToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div ref={toastContainerRef} className="fixed top-20 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            variant={toast.variant}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- hook de contexto junto al provider
export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};
